import { type ReactNode } from "react";
import classNames from "classnames";
import { SmoothImage } from "@components/SmoothImage";
import { EllipsisTooltip } from "./EllipsisTooltip";
import type { AccountProduct } from "./types";

interface ProductOptionProps {
  product: AccountProduct;
  keyword?: string;
  /** 选中态背景，仅 ReplacePopover 列表用 */
  selected?: boolean;
}

/** 把 title 按 keyword 分段，命中段套上 <mark> 标签做高亮 */
const renderHighlight = (title: string, keyword: string): ReactNode => {
  const trimmed = keyword.trim();
  if (!trimmed) return title;
  const lower = title.toLowerCase();
  const k = trimmed.toLowerCase();
  const segments: { text: string; matched: boolean }[] = [];
  let cursor = 0;
  while (cursor < title.length) {
    const idx = lower.indexOf(k, cursor);
    if (idx === -1) {
      segments.push({ text: title.slice(cursor), matched: false });
      break;
    }
    if (idx > cursor) segments.push({ text: title.slice(cursor, idx), matched: false });
    segments.push({ text: title.slice(idx, idx + trimmed.length), matched: true });
    cursor = idx + trimmed.length;
  }
  return segments.map((seg, i) => {
    if (!seg.matched) return <span key={i}>{seg.text}</span>;
    return (
      <mark key={i} className="bg-transparent text-kds-accent-fg">
        {seg.text}
      </mark>
    );
  });
};

export const ProductOption = ({ product, keyword = "", selected }: ProductOptionProps) => {
  const title = product.productTitle ?? "";
  return (
    <div
      className={classNames(
        "flex w-full items-center gap-[8px] rounded-[8px] p-[4px]",
        selected && "bg-kds-accent-soft",
      )}
    >
      <div className="size-[32px] shrink-0 overflow-hidden rounded-[4px] border border-kds-border bg-kds-bg-overlay">
        {product.imageUrl ? (
          <SmoothImage src={product.imageUrl} alt={title || "product"} fit="cover" className="size-full" />
        ) : null}
      </div>
      <EllipsisTooltip className="min-w-0 flex-1" innerClassName="text-[14px] leading-[18px] text-kds-fg" title={title}>
        {renderHighlight(title, keyword)}
      </EllipsisTooltip>
    </div>
  );
};
